import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus, KeyRound, ShieldCheck } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrador" },
  { value: "programmer", label: "Programador" },
  { value: "consultant", label: "Consultor" },
  { value: "accountant", label: "Contador" },
  { value: "employee", label: "Funcionário" },
  { value: "client", label: "Empresa" }
];

const PERMISSION_LABELS = {
  dashboard: "Dashboard",
  companies: "Empresas",
  consultants: "Consultores",
  referrals: "Encaminhamentos",
  results: "Resultados",
  consultancy_requests: "Solicitações",
  users: "Usuários"
};

const roleLabel = (role) => {
  const found = ROLE_OPTIONS.find((item) => item.value === role);
  return found ? found.label : role;
};

const roleBadgeClass = (role) => {
  if (role === "admin") return "bg-navy text-white";
  if (role === "programmer") return "bg-emerald-100 text-emerald-800";
  if (role === "consultant") return "bg-blue-100 text-blue-800";
  if (role === "accountant") return "bg-amber-100 text-amber-800";
  if (role === "employee") return "bg-purple-100 text-purple-800";
  return "bg-slate-100 text-slate-700";
};

const defaultNewUser = {
  name: "",
  email: "",
  password: "",
  role: "client",
  permissions: [],
  active: true
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [permissionKeys, setPermissionKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState(defaultNewUser);
  const [creating, setCreating] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editData, setEditData] = useState({ name: "", email: "", role: "client", permissions: [], active: true });
  const [saving, setSaving] = useState(false);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (search.trim()) params.search = search.trim();
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true, params });
      setUsers(response.data.users || []);
      setPermissionKeys(response.data.permission_keys || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, [roleFilter, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const employeePermissionOptions = useMemo(() => {
    return permissionKeys.map((key) => ({ key, label: PERMISSION_LABELS[key] || key }));
  }, [permissionKeys]);

  const togglePermissions = (targetSetter, state, key) => {
    const current = state.permissions || [];
    const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
    targetSetter({ ...state, permissions: next });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      toast.success("Usuário criado com sucesso");
      setShowCreate(false);
      setNewUser(defaultNewUser);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setEditData({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "client",
      permissions: user.permissions || [],
      active: user.active !== false
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.user_id}`, editData, { withCredentials: true });
      toast.success("Usuário atualizado");
      setShowEdit(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar usuário");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordTarget) return;
    setSavingPassword(true);
    try {
      await axios.put(`${API}/admin/users/${passwordTarget.user_id}/password`, { password: newPassword }, { withCredentials: true });
      toast.success("Senha alterada com sucesso");
      setPasswordTarget(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = async (user) => {
    const ok = window.confirm(`Deseja excluir o usuário ${user.name}?`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}`, { withCredentials: true });
      toast.success("Usuário excluído");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir usuário");
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Gestão de Acessos</h1>
            <p className="text-slate-600">Administre usuários, perfis, senhas e permissões.</p>
          </div>
          <Button className="bg-navy hover:bg-navy/90" onClick={() => setShowCreate(true)} data-testid="admin-open-create-user-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Novo Usuário
          </Button>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                placeholder="Buscar por nome ou e-mail"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-users-search-input"
              />
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="admin-users-role-filter">
                  <SelectValue placeholder="Filtrar por perfil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os perfis</SelectItem>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchUsers} data-testid="admin-users-filter-btn">Aplicar filtros</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Usuários Cadastrados</CardTitle>
            <CardDescription>Total: {users.length}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Carregando usuários...</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum usuário encontrado.</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div key={user.user_id} className="border rounded-lg p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-800">{user.name}</p>
                        <Badge className={roleBadgeClass(user.role)}>{roleLabel(user.role)}</Badge>
                        <Badge variant={user.active ? "default" : "secondary"} className={user.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>
                          {user.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      <p className="text-xs text-slate-500">
                        Empresas vinculadas: {user.companies_count || 0}
                      </p>
                      {user.role === "employee" && (
                        <p className="text-xs text-slate-500">
                          Permissões: {(user.permissions || []).map((key) => PERMISSION_LABELS[key] || key).join(", ") || "Sem permissão"}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => openEdit(user)} data-testid={`admin-edit-user-${user.user_id}`}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPasswordTarget(user)} data-testid={`admin-password-user-${user.user_id}`}>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Senha
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(user)} data-testid={`admin-delete-user-${user.user_id}`}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Senha</Label>
                <Input type="password" minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Perfil</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value, permissions: value === "employee" ? newUser.permissions : [] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between border rounded-md px-3 py-2">
              <Label htmlFor="new-user-active">Usuário ativo</Label>
              <Switch id="new-user-active" checked={newUser.active} onCheckedChange={(checked) => setNewUser({ ...newUser, active: !!checked })} />
            </div>

            {newUser.role === "employee" && (
              <div className="space-y-2 border rounded-md p-3">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Permissões do funcionário
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {employeePermissionOptions.map((item) => (
                    <label key={item.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox
                        checked={(newUser.permissions || []).includes(item.key)}
                        onCheckedChange={() => togglePermissions(setNewUser, newUser, item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button type="submit" className="bg-navy hover:bg-navy/90" disabled={creating}>
                {creating ? "Salvando..." : "Criar usuário"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} required />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Perfil</Label>
                <Select value={editData.role} onValueChange={(value) => setEditData({ ...editData, role: value, permissions: value === "employee" ? editData.permissions : [] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <Label htmlFor="edit-user-active">Usuário ativo</Label>
                <Switch id="edit-user-active" checked={editData.active} onCheckedChange={(checked) => setEditData({ ...editData, active: !!checked })} />
              </div>
            </div>

            {editData.role === "employee" && (
              <div className="space-y-2 border rounded-md p-3">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Permissões do funcionário
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {employeePermissionOptions.map((item) => (
                    <label key={item.key} className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox
                        checked={(editData.permissions || []).includes(item.key)}
                        onCheckedChange={() => togglePermissions(setEditData, editData, item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
              <Button type="submit" className="bg-navy hover:bg-navy/90" disabled={saving}>
                {saving ? "Salvando..." : "Salvar alterações"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passwordTarget} onOpenChange={(open) => !open && setPasswordTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Nova senha</Label>
              <Input
                type="password"
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPasswordTarget(null)}>Cancelar</Button>
              <Button type="submit" className="bg-navy hover:bg-navy/90" disabled={savingPassword}>
                {savingPassword ? "Salvando..." : "Atualizar senha"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
